import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./VerificationPage.css";

function VerificationPage() {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("qr") || "");
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showWelcome, setShowWelcome] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSearch = async (event) => {
    event.preventDefault();

    if (!searchTerm.trim()) {
      setError("Por favor, ingresa un número de documento o código QR.");
      setCertificate(null);
      return;
    }

    setLoading(true);
    setError("");
    setShowWelcome(false);

    try {
      let response = await fetch(
        `${API_BASE_URL}/participants/certificates/by-qr/${searchTerm.trim()}`
      );

      if (!response.ok) {
        response = await fetch(
          `${API_BASE_URL}/participants/certificates/${searchTerm.trim()}`
        );
      }

      if (!response.ok) {
        throw new Error(
          "Certificado no encontrado. Verifica el número de documento o código QR."
        );
      }

      const data = await response.json();
      const foundCertificate = Array.isArray(data) ? data[0] : data;

      if (!foundCertificate) {
        throw new Error(
          "Certificado no encontrado. Verifica el número de documento o código QR."
        );
      }

      setCertificate(foundCertificate);
    } catch (err) {
      setError(err.message);
      setShowWelcome(true);
      console.error("Error al buscar certificado:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!certificate || !certificate.certificate_id) {
      setError("No hay certificado para descargar.");
      return;
    }

    try {
      window.open(
        `${API_BASE_URL}/participants/certificates/${certificate.certificate_id}/generate`,
        "_blank"
      );
    } catch (err) {
      setError(`Error al descargar el certificado: ${err.message}`);
      console.error("Download error:", err);
    }
  };

  const resetSearch = () => {
    setSearchTerm("");
    setCertificate(null);
    setError("");
    setShowWelcome(true);
  };

  return (
    <div className="verification-container">
      <div className="verification-content">
        {/* Sección de Bienvenida */}
        <div
          className={`welcome-section ${showWelcome ? "visible" : "hidden"}`}
        >
          <h1 className="welcome-title">
            Bienvenido al Sistema de Verificación
          </h1>
          <p className="welcome-description">
            Valide la autenticidad de los certificados emitidos por la Fundación
            Universitaria Horizonte de Colombia.
          </p>
        </div>

        <div className="verification-wrapper">
          {/* Formulario */}
          <div className="form-container">
            <div className="verification-card">
              <div className="verification-header">
                <h2>Verificar Certificado</h2>
                <p>Ingrese los datos requeridos para validar</p>
              </div>

              <form onSubmit={handleSearch} className="verification-form">
                <div className="input-group">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Número de documento o Código QR"
                    className="verification-input"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="verification-button"
                  >
                    {loading ? "Verificando..." : "Verificar"}
                  </button>
                </div>
              </form>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>

          {/* Contenedor para Info Universitaria y Resultados */}
          <div className="right-side-container">
            {/* Información Institucional */}
            <div
              className={`institution-info ${
                certificate ? "fade-out" : "fade-in"
              }`}
            >
              <h3>Fundación Universitaria Horizonte de Colombia</h3>
              <p>
                Institución de educación superior comprometida con la excelencia
                académica y la formación integral. Certificamos la participación
                en nuestros programas educativos y eventos académicos.
              </p>
            </div>

            {/* Resultados */}
            <div
              className={`results-container ${
                certificate ? "fade-in" : "fade-out"
              }`}
            >
              {certificate && (
                <div className="expanded-results-card">
                  <div className="results-header">
                    <h2>Certificado Validado</h2>
                    <button onClick={resetSearch} className="close-button">
                      ×
                    </button>
                  </div>

                  <div className="certificate-info-grid">
                    <div className="info-item">
                      <span className="info-label">Evento:</span>
                      <span className="info-value">
                        {certificate.event_name}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Participante:</span>
                      <span className="info-value">
                        {certificate.participant_name}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Documento:</span>
                      <span className="info-value">
                        {certificate.participant_document}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Fecha:</span>
                      <span className="info-value">
                        {new Date(
                          certificate.certification_date
                        ).toLocaleDateString("es-ES", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>

                    {certificate.hours_quantity && (
                      <div className="info-item">
                        <span className="info-label">Horas:</span>
                        <span className="info-value">
                          {certificate.hours_quantity}
                        </span>
                      </div>
                    )}

                    {certificate.presented_work && (
                      <div className="info-item">
                        <span className="info-label">Trabajo:</span>
                        <span className="info-value">
                          {certificate.presented_work}
                        </span>
                      </div>
                    )}

                    <div className="info-item">
                      <span className="info-label">Organizador:</span>
                      <span className="info-value">
                        {certificate.organizer_name}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-label">Código QR:</span>
                      <span className="info-value">{certificate.qr_code}</span>
                    </div>
                  </div>

                  <div className="certificate-footer">
                    <button
                      onClick={handleDownloadPdf}
                      className="download-button"
                    >
                      Descargar Certificado PDF
                    </button>

                    <div className="institution-signature">
                      <p>Fundación Universitaria Horizonte de Colombia</p>
                      <p>Sistema de Verificación de Certificados</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerificationPage;
